<template>
    <ModalContent stitch>
        <template slot="title">{{ $translate("title") }}</template>
        <template slot="content">
            <v-row>
                <v-col cols="12" sm="6">
                    <v-text-field
                        v-model="operator.name"
                        :error-messages="errors.name"
                        :label="$translate('labels.name')"
                        dense
                        autofocus
                        required
                        @keydown="handleKeyPress"
                     outlined />
                </v-col>
                <v-col cols="12" sm="6">
                    <v-text-field
                        v-model="operator.legal_name"
                        :error-messages="errors.legal_name"
                        :label="$translate('labels.legal_name')"
                        dense
                        @keydown="handleKeyPress"
                     outlined />
                </v-col>
                <v-col cols="12" sm="6">
                    <v-text-field
                        v-model="operator.siren"
                        :error-messages="errors.siren"
                        :label="$translate('labels.siren')"
                        dense
                        @keydown="handleKeyPress"
                     outlined />
                </v-col>
                <v-col cols="12" sm="6">
                    <v-text-field
                        v-model="operator.vat_number"
                        :error-messages="errors.vat_number"
                        :label="$translate('labels.vat_number')"
                        dense
                        @keydown="handleKeyPress"
                     outlined />
                </v-col>
            </v-row>
            <AddressForm
                class="mt-2"
                :title="$translate('labels.address')"
                v-model="operator.billing_address"
                :errors="errors.billing_address"
                :highlight-mandatory="true"
                @keydown="handleKeyPress"
            />
        </template>
        <template slot="bottom">
            <Button
                class="my-2 my-sm-4 w-100 w-sm-auto"
                :text="$translate('buttons.cancel')"
                @click="cancelClicked"
                ghost
            />
            <Button
                class="my-2 my-sm-4 w-100 w-sm-auto ml-sm-3"
                :text="$translate('buttons.save')"
                :loading="saving"
                @click="saveClicked"
            />
        </template>
    </ModalContent>
</template>

<script>
import axios from "@/plugins/axios";
import { mapActions } from "vuex";
import GlobalMixins from "@/mixins/global";

const ADDRESS_PREFILL_OMIT = new Set([
    "id",
    "full",
    "latitude",
    "longitude",
    "geocoding_status",
]);

function sanitizeBillingAddressPrefill(raw) {
    if (!raw || typeof raw !== "object") return {};
    const out = {};
    for (const [k, v] of Object.entries(raw)) {
        if (ADDRESS_PREFILL_OMIT.has(k)) continue;
        out[k] = Array.isArray(v) ? [...v] : v;
    }
    return out;
}

function operatorInitialFromParams(params) {
    const prefill = (params && params.operatorPrefill) || {};
    return {
        name: prefill.name != null ? String(prefill.name) : "",
        legal_name: prefill.legal_name != null ? String(prefill.legal_name) : "",
        siren: prefill.siren != null ? String(prefill.siren) : "",
        vat_number:
            prefill.vat_number != null ? String(prefill.vat_number) : "",
        type: prefill.type || "company",
        billing_address: sanitizeBillingAddressPrefill(prefill.billing_address),
    };
}

export default {
    name: "ModalOperatorLogistics",
    mixins: [GlobalMixins],
    props: {
        params: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            operator: operatorInitialFromParams(this.params),
            saving: false,
            errors: {},
        };
    },
    methods: Object.assign(mapActions("mcl", ["hideModal"]), {
        handleKeyPress(event) {
            if (event.key === "Enter") {
                event.preventDefault();
                if (!this.saving) {
                    this.saveClicked();
                }
            }
        },
        cancelClicked() {
            this.hideModal();
        },
        async saveClicked() {
            this.saving = true;
            this.errors = {};
            try {
                const { data } = await axios.post("/api/operators/", this.operator);
                this.hideModal({ result: data });
            } catch (xhr_error) {
                const d = xhr_error.response && xhr_error.response.data;
                if (d && typeof d === "object") {
                    this.errors = d;
                } else {
                    this.addAlert({ text: this.$translate("saveError"), type: "error" });
                }
            } finally {
                this.saving = false;
            }
        },
    }),
    i18n: {
        messages: {
            en: {
                title: "New logistics provider",
                labels: {
                    name: "Name",
                    legal_name: "Legal name",
                    siren: "Company ID (SIREN)",
                    vat_number: "VAT number",
                    address: "Address & notifications",
                },
                buttons: {
                    cancel: "Cancel",
                    save: "Create",
                },
                saveError: "Could not create logistics provider.",
            },
            fr: {
                title: "Nouveau prestataire logistique",
                labels: {
                    name: "Nom",
                    legal_name: "Raison sociale",
                    siren: "SIREN",
                    vat_number: "N° TVA",
                    address: "Adresse et notifications",
                },
                buttons: {
                    cancel: "Annuler",
                    save: "Créer",
                },
                saveError: "Impossible de créer le prestataire logistique.",
            },
        },
    },
};
</script>
